import React from 'react';

import { ButtonProps, GridProps, TextFieldProps } from '@ha/dsl';
import {
  DateRange,
  DayPickerProps,
  RangeModifier,
} from 'ha/ui/next/DatePicker';

// eslint-disable-next-line @typescript-eslint/naming-convention
export enum FOCUSED_FIELD {
  FROM = 'from',
  TO = 'to',
}

// eslint-disable-next-line @typescript-eslint/naming-convention
export enum DATEPICKER_VARIANT {
  INLINE = 'inline',
  DIALOG = 'dialog',
  RESPONSIVE = 'responsive',
}

export interface OverlayProps {
  children: React.ReactElement<DayPickerProps>;
  containerRef: React.RefObject<HTMLDivElement>;
  focused: FOCUSED_FIELD | null;
  from?: Date;
  onFieldFocusChange: (field: FOCUSED_FIELD) => void;
  onOverlayClose: () => void;
  onOverlayExit: () => void;
  open: boolean;
  OverlayActions: React.ReactNode;
  OverlayInfo: React.ReactNode;
  OverlayTitle?: React.ReactNode;
  to?: Date;
}

export interface DatePickerInputsProps {
  from: string;
  fromInputProps: TextFieldProps;
  toInputProps: TextFieldProps;
  to: string;
  isFromFocused: boolean;
  isToFocused: boolean;
  onFromFocus: () => void;
  onToFocus: () => void;
}

export interface DatePickerInputProps {
  inputProps: TextFieldProps;
  onInputFocus: () => void;
  value: string;
}

export interface DateRangePickerProps {
  autoClose?: boolean;
  AdditionalInfo?: React.ReactNode;
  CaptionSubtitle?: (currMonth: Date) => React.ReactNode;
  DatePickerInputs?: React.ComponentType<DatePickerInputsProps>;
  value: DateRange;
  variant?: DATEPICKER_VARIANT;
  clearDatesButton?: string;
  fromInputLabel?: string;
  toInputLabel?: string;
  fromEmptyValueLabel?: string;
  toEmptyValueLabel?: string;
  exclusions?: RangeModifier[];
  datePickerProps?: DayPickerProps;
  initialValidation?: boolean;
  fromInputProps?: TextFieldProps;
  toInputProps?: TextFieldProps;
  onBlur?: () => void;
  onDateChange: (range: DateRange) => void;
  minimumPeriod?: number;
  moveInWindows?: RangeModifier[];
  moveInWindowsTooltip?: string;
  noEndDate?: boolean;
  noEndDateLabel?: string;
  overlayCloseButton?: string;
  buttonProps?: ButtonProps;
  required?: boolean;
  minDate?: Date | string | null;
  maxDate?: Date | string | null;
  header?: React.ReactNode;
}

export interface DatePickerProps {
  AdditionalInfo?: React.ReactNode;
  CaptionSubtitle?: DateRangePickerProps['CaptionSubtitle'];
  clearDateButton?: string;
  DatePickerInput?: React.ComponentType<DatePickerInputProps>;
  datePickerProps?: DayPickerProps;
  inputLabel?: string | React.ReactNode;
  inputProps?: TextFieldProps;
  justify?: GridProps['justify'];
  maxDate?: Date | string | null;
  minDate?: Date | string | null;
  onBlur?: () => void;
  onDateChange: (date: Date | undefined) => void;
  onOverlayClose?: () => void;
  onOverlayOpen?: () => void;
  pickerCaption?: React.ReactNode;
  value?: Date;
}

export interface DateSelectionRules {
  minDate?: Date | string | null;
  maxDate?: Date | string | null;
}
